﻿import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { WorkingDaysService } from '../working-days.service';
import { workingdays, workingDaysScope, workingDaysMonthHistory } from '../../../models/lookups/workingDays.model';
import { DisplayModes } from '../../../models/enums';
import { AlertService, MessageSeverity, DialogType } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { Utilities } from '../../../services/utilities';

// The 7 week days of one scope - company (no ids), a business unit, or a user - with AM/PM switches.
// A change applies from today on; earlier dates (and so earlier reports) keep the setting they had.
@Component({
  selector: 'working-days-editor',
  templateUrl: './working-days-editor.component.html',
  styleUrls: ['./working-days-editor.component.css']
})
export class WorkingDaysEditorComponent implements OnChanges {
  @Input() businessUnitId: number;
  @Input() userId: number;
  @Input() canManage = false;
  /** Drag-and-drop week order (every scope - company, business unit, user - can have its own). */
  @Input() allowReorder = true;
  /** Emits after every saved change or reset (the page uses it to re-check uncovered business units). */
  @Output() changed = new EventEmitter<workingDaysScope>();

  scope: workingDaysScope;
  displayMode: DisplayModes = DisplayModes.Loading;

  // monthly history: the week(s) that were in force during a chosen month, to validate / document past months
  showHistory = false;
  historyYear = new Date().getFullYear();
  historyMonth = new Date().getMonth() + 1;
  history: workingDaysMonthHistory;
  historyLoading = false;
  readonly monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  readonly historyYears = Array.from({ length: 6 }, (_v, i) => new Date().getFullYear() - 4 + i);

  constructor(private alertService: AlertService,
    private workingDaysService: WorkingDaysService,
    private translateService: AppTranslationService) { }

  ngOnChanges() {
    this.load();
    if (this.showHistory)
      this.loadHistory();
  }

  toggleHistory() {
    this.showHistory = !this.showHistory;
    if (this.showHistory)
      this.loadHistory();
  }

  loadHistory() {
    this.historyLoading = true;
    this.workingDaysService.GetMonthHistory(this.historyYear, this.historyMonth, this.businessUnitId, this.userId).subscribe(response => {
      this.history = response as workingDaysMonthHistory;
      this.historyLoading = false;
    }, error => {
      this.historyLoading = false;
      this.alertService.showStickyMessage("Load Error", `Unable to retrieve the working days history.\r\nErrors: "${Utilities.getHttpResponseMessage(error)}"`,
        MessageSeverity.Error, error);
    });
  }

  historySourceLabel(day: workingdays) {
    if (day.source === 'User')
      return 'User exception';
    if (day.source === 'BusinessUnit')
      return day.sourceName;
    return 'Company default';
  }

  get isCompany() {
    return !this.businessUnitId && !this.userId;
  }

  get days(): workingdays[] {
    return this.scope ? this.scope.days : [];
  }

  load() {
    this.displayMode = DisplayModes.Loading;
    this.workingDaysService.GetScope(this.businessUnitId, this.userId).subscribe(response => {
      this.setScope(response as workingDaysScope);
      this.displayMode = DisplayModes.Success;
    }, error => {
      this.displayMode = DisplayModes.Error;
      this.alertService.showStickyMessage("Load Error", `Unable to retrieve working days from the server.\r\nErrors: "${Utilities.getHttpResponseMessage(error)}"`,
        MessageSeverity.Error, error);
    });
  }

  toggle(day: workingdays, isAm: boolean) {
    if (!this.canManage)
      return;
    const change = {
      businessUnitId: this.businessUnitId,
      userId: this.userId,
      workingDayId: day.id,
      amActive: isAm ? !day.amActive : day.amActive,
      pmActive: isAm ? day.pmActive : !day.pmActive
    };
    this.workingDaysService.SetDay(change).subscribe(response => {
      this.setScope(response as workingDaysScope);
      this.alertService.showMessage("", this.translateService.getTranslation("SavedSuccessfully"), MessageSeverity.Success);
      this.changed.emit(this.scope);
      if (this.showHistory) this.loadHistory();
    }, error => {
      this.alertService.showStickyMessage("Save Error", `Unable to save the working day.\r\nErrors: "${Utilities.getHttpResponseMessage(error)}"`,
        MessageSeverity.Error, error);
      this.load();
    });
  }

  confirmReset() {
    this.alertService.showDialog("Remove these own working days from today on? Past dates keep them; from today the parent / company days apply.", DialogType.Confirm,
      () => this.reset());
  }

  reset() {
    this.workingDaysService.ResetScope(this.businessUnitId, this.userId).subscribe(response => {
      this.setScope(response as workingDaysScope);
      this.alertService.showMessage("", this.translateService.getTranslation("SavedSuccessfully"), MessageSeverity.Success);
      this.changed.emit(this.scope);
      if (this.showHistory) this.loadHistory();
    }, error => {
      this.alertService.showStickyMessage("Save Error", `Unable to remove the working days.\r\nErrors: "${Utilities.getHttpResponseMessage(error)}"`,
        MessageSeverity.Error, error);
    });
  }

  drop(event: CdkDragDrop<workingdays[]>) {
    if (!this.allowReorder || !this.canManage || event.previousIndex === event.currentIndex)
      return;
    moveItemInArray(this.scope.days, event.previousIndex, event.currentIndex);
    this.workingDaysService.ReorderScope(this.businessUnitId, this.userId, this.scope.days.map(d => d.id)).subscribe(response => {
      this.setScope(response as workingDaysScope);
      this.alertService.showMessage("", this.translateService.getTranslation("SavedSuccessfully"), MessageSeverity.Success);
      this.changed.emit(this.scope);
      if (this.showHistory) this.loadHistory();
    }, error => {
      this.alertService.showStickyMessage("Save Error", `Unable to save the new order.\r\nErrors: "${Utilities.getHttpResponseMessage(error)}"`,
        MessageSeverity.Error, error);
      this.load();
    });
  }

  sourceLabel(day: workingdays) {
    if (day.source === 'User')
      return 'User exception';
    if (day.source === 'BusinessUnit')
      return (this.businessUnitId && day.sourceBusinessUnitId === this.businessUnitId) ? 'Own' : `Inherited from ${day.sourceName}`;
    return 'Company default';
  }

  private setScope(scope: workingDaysScope) {
    scope.days = (scope.days || []).sort((a, b) => a.order - b.order);
    this.scope = scope;
  }
}
